<template>
  <div>

    <svg style="display: none;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <defs>
        <path id="input-lock-icon" d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z"></path>
      </defs>
    </svg>

    <div>
      <div
        v-if="loading"
        class="text-normal flex flex-align-center"
      >
        <span class="text-smaller">Loading Templates</span>
        <SvgSpinner size="1.5em" style="margin-left: .5em;" />
      </div>
      <div
        v-else-if="Object.keys(templates).length === 0"
        class="text-normal flex flex-align-center"
      >
        <SvgWarning size="1em"/>
        <em class="text-smaller" style="margin-left: 0.35em;">
          There are no templates for this ear&nbsp;tag.
        </em>
      </div>
      <div v-else>
        <label class="sr-only">Select a template</label>

        <CustomHtmlSelect
          :disabled="disabled"
          :items="sortedTemplates"
          :selected="activeTemplate"
          @update:selected="$emit('update:selectedTemplate', $event.label)"
        >
          <template #active="slotProps">
            <SelectTemplateItem :item="slotProps.selected"/>
          </template>

          <template #item="slotProps">
            <SelectTemplateItem
              :item="slotProps.item"
              :class="{'current-item': slotProps.current}"
            />
          </template>
        </CustomHtmlSelect>
        <div
          class="text-smaller text-italic"
          :class="{'invisible': disabled}"
          style="margin-top: .25em;"
        >
          Choose a template above.
        </div>
      </div>
    </div>

    <div style="padding-top: 1em;">
      <div class="flex-row row">
        <div
          class="flex flex-col col-xs-12"
          :class="{'col-md-6': !singleColumn}"
        >

          <div
            v-if="selectedTemplate !== ''"
            class="flex-grow"
            style="max-width: 100%; width: 100%;"
          >
            <div v-if="builderLines.length > 0">
              <component
                ref="lines"
                v-for="(l, i) in builderLines"
                :index="i"
                :is="`${slugify(l.line.type)}-field`"
                :key="`line-${i}-${l.line.id}`"
                :line.sync="localLines[l.index]"
                :logo="logo"
                :quantity="quantity"
                :scrapie="scrapie"
                :eid="eid"
                :disabled="disabled"
                @child-error="childError = $event"
                v-on="$listeners"
              />
            </div>
            <div
              v-else
              class="text-center flex flex-align-center"
              style="height: 100%;"
            >
              <div class="text-larger text-normal flex-grow" style="padding-bottom: 2em; padding-top: 2em; margin: auto;">
                This template is&nbsp;blank.
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-center flex flex-align-center"
            style="height: 100%;"
          >
            <div class="text-larger text-normal flex-grow" style="padding-bottom: 2em; padding-top: 2em;">
              Select a template to customize your&nbsp;tags.
            </div>
          </div>

        </div>
        <div
          class="flex-col col-xs-12"
          :class="{'col-md-6': !singleColumn}"
        >

          <EartagCanvas
            ref="artboard"
            class="canvas"
            :image="image"
            :lines="drawLines"
            :debugging="false"
            v-on="$listeners"
          />
          <div
            v-if="artboardError()"
            class="text-red text-tiny"
            style="padding: .35em;"
          >
            {{ artboardError() }}
          </div>
          <div
            v-if="imageRowWarning"
            class="text-tiny no-margin-bottom"
            style="margin-top: .25em;"
            v-html="imageRowWarning"
          ></div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
import EartagCanvas from './EartagCanvas';
import CustomHtmlSelect from './CustomHtmlSelect';
import SelectTemplateItem from './SelectTemplateItem';
import SvgSpinner from './SvgSpinner';
import SvgWarning from './SvgWarning';
import TemplateAinInput from './TemplateAinInput';
import TemplateAinBtnInput from './TemplateAinBtnInput';
import TemplateAinLast5Input from './TemplateAinLast5Input';
import TemplateAinMultiLineInput from './TemplateAinMultiLineInput';
import TemplateBlankInput from './TemplateBlankInput';
import TemplateFlockInput from './TemplateFlockInput';
import TemplateImageInput from './TemplateImageInput';
import TemplateSequenceInput from './TemplateSequenceInput';
import TemplateTextInput from './TemplateTextInput';
import { throttle } from '../helpers.js';
import { kebabCase } from 'lodash';

export default {
  components: {
    EartagCanvas,
    CustomHtmlSelect,
    'ain-field': TemplateAinInput,
    'ain-btn-field': TemplateAinBtnInput,
    'ain-last-5-field': TemplateAinLast5Input,
    'ain-multi-line-field': TemplateAinMultiLineInput,
    'blank-field': TemplateBlankInput,
    'flock-field': TemplateFlockInput,
    'image-field': TemplateImageInput,
    'sequence-field': TemplateSequenceInput,
    SelectTemplateItem,
    SvgSpinner,
    SvgWarning,
    'text-field': TemplateTextInput,
  },

  props: {
    eid: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    image: {
      type: String,
      required: true,
    },

    lines: {
      default: () => [],
      type: Array,
    },

    loading: {
      default: false,
      type: Boolean,
    },

    logo: {
      default: () => {},
      type: Object,
    },

    quantity: {
      type: Number,
      default: 0,
    },

    scrapie: {
      default: false,
      type: Boolean,
    },

    sequence: {
      default: '',
      type: String,
    },

    sequences: {
      default: () => [],
      type: Array,
    },

    selectedTemplate: {
      default: '',
      type: String,
    },

    singleColumn: {
      default: false,
      type: Boolean,
    },

    templates: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      localLines: this.lines,
      childError: false,
      filled: true,
      errors: {},
    };
  },

  methods: {
    artboardError() {
      return ((this.$refs['artboard'] || {}).errors || {}).image || '';
    },

    slugify: (str) => kebabCase(str),
  },

  computed: {
    activeTemplate() {
      return this.selectedTemplate ? Object.values(this.templates).find(template => template.label == this.selectedTemplate) : null;
    },

    isComplete() {
      return this.disabled || (!this.childError && this.filled);
    },

    builderLines() {
      return this.lines.slice()
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => line.visible_in_builder);
    },

    drawLines() {
      return this.lines.slice().map(l => {
        let line = Object.assign({}, l);

        if(line.type === 'flock') {
          line.value = (this.$root.$refs['eartag-app'].flock || {}).id || '';
        }

        if(line.type === 'ain_last5') {
          line.value = '12345';
        }

        if(line.type === 'ain_multi_line') {
          line.value = '840 000 0000\n12345';
        }

        return line;
      });
    },

    imageRowWarning() {
      if(this.lines.filter(l => l.type === 'image').length > 0) {
        return '<em>*We are unable to generate a preview image using a custom logo.</em>';
      }

      return;
    },

    sortedTemplates() {
      return Object.values(this.templates)
        .sort((a, b) => a.position - b.position);
    },
  },

  watch: {
    lines: {
      deep: true,
      handler(lines) {
        this.localLines = lines;
      },
    },

    localLines: {
      deep: true,
      handler(lines) {
        this.$emit('update:lines', lines);

        const unfilled = lines.reduce((carry, line) => {
          if(['sequence', 'text'].indexOf(line.type) !== -1 && !line.value) {
            carry++;
          }

          return carry;
        }, 0);

        this.filled = unfilled > 0 ? false : true;
      },
    },

    isComplete: {
      handler(value) {
        this.$emit('builder-done', value);
      },
      immediate: true,
    },

    selectedTemplate(template) {
      let foundTemplate = (template !== '') ? Object.values(this.templates).find(t => t.label == template) : null;
      let newLines = foundTemplate ? foundTemplate.lines.slice(0) : [];

      this.localLines = newLines.map(l => {
        let extend = {};

        if(l.type === 'sequence' && this.scrapie) {
          extend = {value: this.sequence};
        }

        return Object.assign({}, l, extend);
      });
    },

    sequences: {
      deep: true,
      handler() {
        const rows = (this.$refs.lines || []);

        for(let i = 0; i < rows.length; i++) {
          if(typeof rows[i].triggerChange !== 'undefined') {
            rows[i].triggerChange();
          }
        }
      },
    },

    templates: {
      handler() {
        this.childError = false;

        if(!Object.values(this.templates).find(template => template.label == this.selectedTemplate)) {
          this.$emit('update:selectedTemplate', Object.values(this.templates)[0].label);
        }
      },
      immediate: true,
    },
  },
}
</script>

<style scoped>
.canvas {
  background-color: #FFFFFF;
  border: 1px solid #F2F3F4;
}

.foot {
  border-top: 1px solid #c1c1c1;
  margin-top: .25em;
  padding: .75em 1em;
}

>>> .error-text {
  color: #953b39;
  font-size: 0.875em;
  font-weight: 400;
  margin-top: .25em;
}

>>> .error {
  border-bottom: 1px solid #953b39;
  margin-bottom: .5em;
  padding-bottom: .5em;
}

>>> input:disabled, input:disabled:hover {
  background: transparent;
  box-shadow: none;
  outline: none;
  opacity: 1;
  cursor: not-allowed;
}

>>> input:read-only {
  background-color: transparent;
  box-shadow: none;
  padding-right: 1.75em;
}

>>> input:read-only:hover {
  box-shadow: none;
}
</style>
